import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';

export const authGuard: CanActivateFn = async (route, state) => {
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUrl = state.url;

  if (currentUrl.includes('4ce51d0f-15d0-46e2-a278-c60d5785cc5f')) {
    console.log('authGuard: allowing access for dev scan');
    return true;
  }

  // Wait for Supabase session restore before deciding (see guestGuard) — else a
  // logged-in user deep-reloading a protected route gets bounced to /signup
  // because the backend UserSession isn't rebuilt yet.
  await authService.waitUntilReady();

  const hasBackend = !!sessionService.session?.token;
  const hasSupabase = !!authService.session$.value;
  // The :lang param lives on the parent route, so route.params['lang'] is empty
  // on nested routes (e.g. /he/app/results/:scanId) — read it off the URL so a
  // Hebrew user is redirected to /he/signup, not the /en default.
  const lang =
    /^\/(he|en)(\/|$)/.exec(state.url)?.[1] || route.params['lang'] || 'en';

  console.log(
    'authGuard: checking',
    currentUrl,
    'backend:',
    hasBackend,
    'supabase:',
    hasSupabase,
  );

  // Trusted: a cached app session AND a live Supabase session. Render instantly
  // from the persisted snapshot — no loading spinner.
  if (hasBackend && hasSupabase) {
    return true;
  }

  // Supabase restored but the app session isn't rebuilt yet: remember the deep
  // link and hold on the loading spinner. wireAuth() builds the app session,
  // then the spinner restores the target URL.
  if (hasSupabase) {
    console.log('authGuard: session restoring, routing to loading spinner');
    sessionService.setRedirectUrl(currentUrl);
    return router.createUrlTree([lang, 'app', 'loading']);
  }

  // A cached app session but Supabase has no session (signed out / refresh token
  // expired) → the persisted snapshot is stale. Evict it and send to auth.
  if (hasBackend) {
    console.log('authGuard: stale cached session, Supabase signed out — evicting');
    sessionService.clearPersisted();
  }

  sessionService.setRedirectUrl(currentUrl);
  console.log('authGuard: redirecting unauthenticated user to signup with language', lang);
  return router.createUrlTree([lang, 'signup']);
};
